// 产品列表与详情状态
import { getPortalProductDetail, getPortalProductIndex } from '@/api/portal'
import { normalizeProductDetail, normalizeProductIndex } from '@/components/modules/products/utils/productNormalize'
import {
  getProductCategoryLoadingKey,
  resolveProductCategoryFromPath,
  findPortalCategory,
  findPortalCategoryRoot
} from '@/components/modules/products/utils/productHelpers'

const detailFetchSeqMap = new Map()
const inflightCategoryRequests = new Map()
const inflightDetailRequests = new Map()
const defaultLocaleCertIndexCache = new Map()

function resolveDefaultLocaleCode(rootState, currentLocale) {
  const locales = rootState.locale?.locales || []
  const fallback = locales.find((item) => item.isDefault)?.code || 'zh-CN'
  return fallback && fallback !== currentLocale ? fallback : ''
}

function mergeDefaultLocaleCerts(items, fallbackItems) {
  const byGroup = new Map()
  const byId = new Map()
  ;(fallbackItems || []).forEach((item) => {
    const certs = item?.certifications
    if (!Array.isArray(certs) || !certs.length) return
    if (item.groupId != null && item.groupId !== '') {
      byGroup.set(String(item.groupId), certs)
    }
    if (item.id != null && item.id !== '') {
      byId.set(String(item.id), certs)
    }
  })
  return (items || []).map((item) => {
    if (Array.isArray(item?.certifications) && item.certifications.length) return item
    const fromGroup = item?.groupId != null && item.groupId !== ''
      ? byGroup.get(String(item.groupId))
      : null
    const fromId = item?.id != null && item.id !== ''
      ? byId.get(String(item.id))
      : null
    const certs = fromGroup || fromId
    return certs?.length ? { ...item, certifications: certs } : item
  })
}

function rememberDefaultLocaleCertIndex(locale, rootState, items) {
  const locales = rootState.locale?.locales || []
  const fallback = locales.find((item) => item.isDefault)?.code || 'zh-CN'
  if (locale && locale === fallback) {
    defaultLocaleCertIndexCache.set(locale, items)
  }
}

function loadDefaultLocaleCertIndex(defaultLocale) {
  if (!defaultLocale) return []
  return defaultLocaleCertIndexCache.get(defaultLocale) || []
}

// 产品按标题拼音排序
function sortProducts(a, b) {
  return String(a.title || '').localeCompare(String(b.title || ''), 'zh-CN')
}

// 按 id 去重产品列表
function dedupeProductsById(items) {
  const map = new Map()
  ;(items || []).forEach((item) => {
    if (!item?.id) return
    map.set(String(item.id), item)
  })
  return Array.from(map.values()).sort(sortProducts)
}

// 统一解析分类拉取参数（兼容旧的字符串 categoryId）
function normalizeCategoryPayload(payload) {
  if (payload == null || payload === '') {
    return { categoryId: '', force: false }
  }
  if (typeof payload === 'string' || typeof payload === 'number') {
    return { categoryId: String(payload), force: false }
  }
  return {
    categoryId: payload.categoryId != null && payload.categoryId !== ''
      ? String(payload.categoryId)
      : '',
    force: Boolean(payload.force)
  }
}

// 合并分类产品到全局列表
function mergeCategoryProducts(existingItems, categoryId, incomingItems) {
  const categoryKey = String(categoryId)
  const incomingIds = new Set(
    (incomingItems || []).map((item) => String(item.id)).filter(Boolean)
  )
  // preserved
  const preserved = (existingItems || []).filter((item) => {
    const id = String(item.id)
    if (incomingIds.has(id)) return false
    return String(item.categoryId) !== categoryKey
  })
  return dedupeProductsById([...preserved, ...(incomingItems || [])])
}

function normalizeDetailPayload(payload) {
  if (payload != null && typeof payload === 'object' && !Array.isArray(payload)) {
    return {
      id: payload.id != null && payload.id !== '' ? String(payload.id) : '',
      locale: payload.locale ? String(payload.locale) : ''
    }
  }
  return {
    id: payload != null && payload !== '' ? String(payload) : '',
    locale: ''
  }
}

function isCurrentLocale(rootState, locale) {
  return Boolean(locale) && rootState.locale?.current === locale
}

export default {
  namespaced: true,
  state: {
    items: [],
    itemsLocale: '',
    categoryLoading: {},
    categoryLoaded: {},
    detailMap: {},
    detailLocale: '',
    detailLoadingMap: {}
  },
  getters: {
    allItems(state) {
      return state.items
    },
    getById: (state) => (id) => state.items.find((item) => String(item.id) === String(id)) || null,
    getDetailById: (state) => (id) => {
      if (!id) return null
      const key = String(id)
      return Object.prototype.hasOwnProperty.call(state.detailMap, key)
        ? state.detailMap[key]
        : undefined
    },
    isDetailLoading: (state) => (id) => Boolean(state.detailLoadingMap[String(id)]),
    isCategoryLoading: (state) => (key) => Boolean(state.categoryLoading[key]),
    filterByCategoryId: (state) => (categoryId) => {
      if (!categoryId) return state.items
      return state.items.filter((item) => String(item.categoryId) === String(categoryId))
    }
  },
  mutations: {
    SET_ITEMS(state, payload) {
      const items = Array.isArray(payload) ? payload : payload?.items
      const locale = Array.isArray(payload) ? '' : (payload?.locale || '')
      state.items = items || []
      if (locale) state.itemsLocale = locale
    },
    SET_CATEGORY_LOADING(state, { key, loading }) {
      state.categoryLoading = {
        ...state.categoryLoading,
        [key]: loading
      }
    },
    SET_CATEGORY_LOADED(state, { key, loaded }) {
      state.categoryLoaded = {
        ...state.categoryLoaded,
        [key]: loaded
      }
    },
    RESET_FOR_LOCALE(state) {
      state.items = []
      state.itemsLocale = ''
      state.categoryLoading = {}
      state.categoryLoaded = {}
      state.detailMap = {}
      state.detailLocale = ''
      state.detailLoadingMap = {}
    },
    CLEAR_DETAIL_CACHE(state) {
      state.detailMap = {}
      state.detailLocale = ''
      state.detailLoadingMap = {}
    },
    SET_DETAIL(state, { id, detail, locale }) {
      const key = String(id)
      if (locale && state.detailLocale !== locale) {
        state.detailMap = { [key]: detail }
        state.detailLocale = locale
        return
      }
      state.detailMap = {
        ...state.detailMap,
        [key]: detail
      }
      if (locale) state.detailLocale = locale
    },
    SET_DETAIL_LOADING(state, { id, loading }) {
      state.detailLoadingMap = {
        ...state.detailLoadingMap,
        [String(id)]: loading
      }
    }
  },
  actions: {
    resetForLocaleChange({ commit }) {
      commit('RESET_FOR_LOCALE')
    },
    // 按分类拉取产品列表
    async fetchProductsByCategory({ commit, state, rootState }, payload = '') {
      const locale = rootState.locale.current
      if (!locale) return

      const { categoryId, force } = normalizeCategoryPayload(payload)
      const loadingKey = getProductCategoryLoadingKey(categoryId)
      const localeReady = state.itemsLocale === locale
      const allLoaded = localeReady && state.categoryLoaded[getProductCategoryLoadingKey('')]
      if (!force && localeReady && (state.categoryLoaded[loadingKey] || (categoryId && allLoaded))) return

      const requestKey = JSON.stringify({ locale, categoryId: loadingKey })

      if (inflightCategoryRequests.has(requestKey)) {
        return inflightCategoryRequests.get(requestKey)
      }

      // task
      const task = (async () => {
        commit('SET_CATEGORY_LOADING', { key: loadingKey, loading: true })
        try {
          const defaultLocale = resolveDefaultLocaleCode(rootState, locale)
          const data = await getPortalProductIndex(locale, categoryId || undefined)
          if (!isCurrentLocale(rootState, locale)) return

          const normalized = normalizeProductIndex(data)
          rememberDefaultLocaleCertIndex(locale, rootState, normalized)
          const withCerts = mergeDefaultLocaleCerts(
            normalized,
            loadDefaultLocaleCertIndex(defaultLocale),
          )

          if (categoryId) {
            const baseItems = state.itemsLocale === locale ? state.items : []
            commit('SET_ITEMS', {
              items: mergeCategoryProducts(baseItems, categoryId, withCerts),
              locale
            })
          } else {
            commit('SET_ITEMS', { items: dedupeProductsById(withCerts), locale })
          }
          commit('SET_CATEGORY_LOADED', { key: loadingKey, loaded: true })
        } finally {
          if (isCurrentLocale(rootState, locale)) {
            commit('SET_CATEGORY_LOADING', { key: loadingKey, loading: false })
          }
        }
      })()

      inflightCategoryRequests.set(requestKey, task)
      try {
        await task
      } finally {
        inflightCategoryRequests.delete(requestKey)
      }
    },
    reloadByPath({ dispatch, rootGetters }, payload) {
      const path = typeof payload === 'string' ? payload : payload?.path
      const force = typeof payload === 'object' && payload ? Boolean(payload.force) : false
      const productsRootPath = typeof payload === 'object' && payload ? (payload.productsRootPath || '') : ''
      const categoryId = resolveProductCategoryFromPath(path, productsRootPath)
      if (!categoryId) {
        return dispatch('fetchProductsByCategory', { categoryId: '', force })
      }
      const categories = rootGetters['productCategories/enabledItems'] || []
      const root = findPortalCategoryRoot(categories, categoryId)
      // 一级下有二级时拉全量，再由页面按树过滤；否则按当前分类拉取
      if (root?.children?.length) {
        return dispatch('fetchProductsByCategory', { categoryId: '', force })
      }
      const category = findPortalCategory(categories, categoryId)
      return dispatch('fetchProductsByCategory', {
        categoryId: category?.id || categoryId,
        force
      })
    },
    async fetchProductIndex({ dispatch }) {
      return dispatch('fetchProductsByCategory', { categoryId: '', force: false })
    },
    async fetchProductDetail({ commit, rootState, state }, payload) {
      const parsed = normalizeDetailPayload(payload)
      const locale = parsed.locale || rootState.locale.current
      const key = parsed.id
      if (!locale || !key) return null

      if (state.detailLocale === locale && Object.prototype.hasOwnProperty.call(state.detailMap, key)) {
        return state.detailMap[key]
      }

      const requestKey = `${locale}:${key}`
      if (inflightDetailRequests.has(requestKey)) {
        return inflightDetailRequests.get(requestKey)
      }

      const seq = (detailFetchSeqMap.get(requestKey) || 0) + 1
      detailFetchSeqMap.set(requestKey, seq)

      const task = (async () => {
        commit('SET_DETAIL_LOADING', { id: key, loading: true })
        try {
          const data = await getPortalProductDetail(key, locale)
          if (detailFetchSeqMap.get(requestKey) !== seq) return null
          if (!isCurrentLocale(rootState, locale)) return null
          const detail = normalizeProductDetail(data)
          commit('SET_DETAIL', { id: key, detail, locale })
          const resolvedId = detail?.id != null && detail.id !== '' ? String(detail.id) : ''
          if (resolvedId && resolvedId !== key) {
            commit('SET_DETAIL', { id: resolvedId, detail, locale })
          }
          return detail
        } catch (error) {
          if (detailFetchSeqMap.get(requestKey) !== seq) return null
          if (!isCurrentLocale(rootState, locale)) return null
          commit('SET_DETAIL', { id: key, detail: null, locale })
          throw error
        } finally {
          if (detailFetchSeqMap.get(requestKey) === seq && isCurrentLocale(rootState, locale)) {
            commit('SET_DETAIL_LOADING', { id: key, loading: false })
          }
        }
      })()

      inflightDetailRequests.set(requestKey, task)
      try {
        return await task
      } finally {
        inflightDetailRequests.delete(requestKey)
      }
    }
  }
}
